import { useEffect, useId, useState } from "react";
import { InputText } from "@/deps/ui-library";
import { useLedgerDocuments } from "@/api/queries";
import { formatDate } from "@/lib/dates";
import { formatAmount } from "@/lib/money";

/**
 * The general ledger's search: as somebody types, the documents that match in the chosen ledger and
 * dates. Typing never touches the table. Enter or a click loads the result, and takes any other
 * filters waiting to be applied with it.
 *
 * Its own listbox on RDS's input, as `PartyCombobox` is: focus stays in the input so somebody can keep
 * typing while arrowing down, and Enter with nothing highlighted means the words typed, not an option.
 */
export function VoucherSearch({
  value,
  accountId,
  from,
  to,
  onChange,
  onSubmit,
}: {
  value: string;
  accountId: string | undefined;
  from: string;
  to: string;
  onChange: (value: string) => void;
  /** With a document's number when one was picked, or with nothing for the words typed. */
  onSubmit: (documentNumber?: string) => void;
}) {
  const id = useId();
  const listId = `${id}-list`;
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [term, setTerm] = useState(value.trim());

  // A keystroke per request would turn one voucher number into eleven requests.
  useEffect(() => {
    const timer = setTimeout(() => setTerm(value.trim()), 150);
    return () => clearTimeout(timer);
  }, [value]);

  const { data: found } = useLedgerDocuments(
    { accountId: accountId ?? "", from, to, q: term },
    open && term !== "" && accountId !== undefined && from <= to,
  );
  const matches = found ?? [];
  const highlighted = open ? matches[active] : undefined;

  const close = () => {
    setOpen(false);
    setActive(-1);
  };
  const submit = (documentNumber?: string) => {
    close();
    onSubmit(documentNumber);
  };

  return (
    <div className="relative">
      <InputText.FilterSearch
        role="combobox"
        aria-label="Search voucher or narration"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={highlighted ? `${listId}-${active}` : undefined}
        autoComplete="off"
        placeholder="Search voucher or narration"
        value={value}
        onChange={(event) => {
          onChange(event.target.value);
          setActive(-1);
          setOpen(event.target.value.trim() !== "");
        }}
        onClear={() => {
          onChange("");
          close();
        }}
        onFocus={() => setOpen(value.trim() !== "")}
        onBlur={close}
        onKeyDown={(event) => {
          if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            event.preventDefault();
            if (matches.length === 0) return;
            setOpen(true);
            setActive((current) =>
              event.key === "ArrowDown"
                ? Math.min(current + 1, matches.length - 1)
                : Math.max(current - 1, -1),
            );
          } else if (event.key === "Enter") {
            event.preventDefault();
            submit(highlighted?.number);
          } else if (event.key === "Escape") {
            close();
          }
        }}
      />
      {open && value.trim() !== "" ? (
        <div
          id={listId}
          role="listbox"
          aria-label="Matching documents"
          className="border-border bg-popover absolute z-floating mt-1 max-h-80 w-[360px] overflow-auto rounded-md border p-1 shadow-md"
        >
          {matches.length === 0 ? (
            <p className="text-muted-foreground px-2 py-2 text-xs">
              No documents match. Press Enter to search narration.
            </p>
          ) : (
            matches.map((match, index) => (
              <div
                key={match.journalId}
                id={`${listId}-${index}`}
                role="option"
                tabIndex={-1}
                aria-selected={index === active}
                onMouseDown={(event) => {
                  // mousedown, not click: the input's blur would close the list first.
                  event.preventDefault();
                  submit(match.number);
                }}
                className={[
                  "text-foreground grid cursor-pointer grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 rounded-sm px-2 py-1.5 text-sm",
                  index === active ? "bg-muted" : "hover:bg-muted",
                ].join(" ")}
              >
                <span className="num font-medium whitespace-nowrap">{match.number}</span>
                <span className="text-muted-foreground truncate text-xs">
                  {formatDate(match.date)}
                  {match.narration ? ` · ${match.narration}` : ""}
                </span>
                <span className="num whitespace-nowrap">{formatAmount(match.amount)}</span>
              </div>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}
